import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Toast from '../../Shared/Toast';
import Tooltip from '../ItemTooltip';
import './SellOrderForm.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import dayjs from 'dayjs';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

interface ItemInstance {
  itemInstanceId: number;
  runes: Array<{
    runeSlotIndex: number;
    rune: { name: string; runeColor: string; enchantModifiers?: string };
  }>;
}

interface InventoryItem {
  id: number;
  itemId: number;
  quantity: number;
  itemInstanceId?: number | null;
  instances: ItemInstance[];
  Item: {
    id: number;
    name: string;
    itemCategory: string;
    minDamage?: number | null;
    maxDamage?: number | null;
    dmgCap?: number | null;
    absorption?: number | null;
    blockValue?: number | null;
    bonuses?: string | null;
    strengthRequirement?: number | null;
    weaponType?: string | null;
    runeSlots?: string[] | null;
  };
}

const EQUIPPABLE_CATEGORIES = ['Weapon', 'Shield', 'Armor', 'Accessory'];

interface Listing {
  price: number;
  quantity: number;
  itemName?: string;
  Item?: {
    name: string;
  };
}

interface BuyOrderListing {
  price_offer: number;
  quantity: number;
  Item?: { id: number; name: string };
}

const DURATION_OPTIONS = [
  { hours: 24,  fee: 2,  label: '1 day',  desc: '1 day'  },
  { hours: 72,  fee: 5,  label: '3 days', desc: '3 days' },
  { hours: 168, fee: 10, label: '7 days', desc: '7 days' },
];

const SellOrderForm: React.FC = () => {
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState(24);
  const [currentListings, setCurrentListings] = useState<Listing[]>([]);
  const [currentBuyOrders, setCurrentBuyOrders] = useState<BuyOrderListing[]>([]);
  const [salesData, setSalesData] = useState<{ date: string; itemsSold: number; avgPrice: number }[]>([]);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [submitting, setSubmitting] = useState(false);
  const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null);

  const { user } = useAuth();
  const { mercenary } = useMercenary();

  const fetchInventory = async () => {
    try {
      if (mercenary?.id) {
        const response = await axios.get(`/api/auction/inventory/${mercenary.id}`);
        setInventory(response.data);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  useEffect(() => {
    fetchInventory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mercenary]);

  useEffect(() => {
    if (selectedItemId) {
      axios
        .get(`/api/auction/listings/${selectedItemId}`)
        .then((response) => setCurrentListings(response.data))
        .catch((error) => console.error('Error fetching listings:', error));

      axios
        .get(`/api/auction/orders/active-bo/${selectedItemId}`)
        .then((response) => setCurrentBuyOrders(response.data))
        .catch((error) => console.error('Error fetching buy orders:', error));

      axios
        .get(`/api/auction/sales/${selectedItemId}`)
        .then((response) => setSalesData(response.data))
        .catch((error) => console.error('Error fetching sales history:', error));
    } else {
      setCurrentListings([]);
      setCurrentBuyOrders([]);
    }
  }, [selectedItemId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !mercenary || !selectedItemId) {
      displayToast('User, mercenary, or item selection is missing.', 'error');
      return;
    }

    const selectedItem = inventory.find(i => i.itemId === parseInt(selectedItemId));
    const isEquippable = selectedItem ? EQUIPPABLE_CATEGORIES.includes(selectedItem.Item?.itemCategory) : false;

    if (isEquippable && selectedItem && selectedItem.instances.length > 1 && !selectedInstanceId) {
      displayToast('Please select which copy you want to sell.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload: Record<string, any> = {
        seller_id: mercenary.id,
        seller_user_id: user.id,
        item_id: parseInt(selectedItemId),
        price: parseInt(price),
        duration,
        quantity: selectedQuantity,
      };
      if (isEquippable && selectedInstanceId) {
        payload.itemInstanceId = selectedInstanceId;
      }
      await axios.post('/api/auction/sellorder/create', payload);

      displayToast('Sell order created successfully!', 'success');
      setSelectedItemId('');
      setSelectedQuantity(1);
      setSelectedInstanceId(null);
      setPrice('');
      setDuration(24);

      fetchInventory();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to create sell order';
      displayToast(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const selectedFee = DURATION_OPTIONS.find(d => d.hours === duration)?.fee || 5;

  const last30Days = Array.from({ length: 30 }, (_, index) =>
    dayjs().subtract(29 - index, 'day').format('YYYY-MM-DD')
  );

  const salesValues = last30Days.map((date) => {
    const sale = salesData.find((s) => s.date === date);
    return sale ? sale.itemsSold : 0;
  });

  const chartData = {
    labels: last30Days.map(d => dayjs(d).format('MM/DD')),
    datasets: [
      {
        label: 'Items Sold',
        data: salesValues,
        backgroundColor: 'rgba(var(--color-gold-rgb), 0.4)',
        borderColor: 'rgba(var(--color-gold-rgb), 0.7)',
        borderWidth: 1,
        borderRadius: 2,
      },
    ],
  };

  const chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'category',
        ticks: { color: '#777', font: { size: 9 }, maxRotation: 45 },
        grid: { color: 'rgba(255,255,255,0.03)' },
      },
      y: {
        type: 'linear',
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: (value) => Number(value).toFixed(0),
          color: '#777',
          font: { size: 10 },
        },
        grid: { color: 'rgba(255,255,255,0.05)' },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(20,20,30,0.95)',
        borderColor: 'rgba(212,175,55,0.3)',
        borderWidth: 1,
        titleFont: { size: 11 },
        bodyFont: { size: 11 },
        callbacks: {
          label: (context) => `Sold: ${salesValues[context.dataIndex]}`,
          footer: (context) => {
            const date = last30Days[context[0].dataIndex];
            const sale = salesData.find((s) => s.date === date);
            const avgPrice = sale && !isNaN(Number(sale.avgPrice)) ? Number(sale.avgPrice).toFixed(0) : '0';
            return `Avg Price: ${avgPrice} silver`;
          },
        },
      },
    },
  };

  return (
    <div className="sell-order-form-container">
      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}

      {/* ── Left: Create Form ── */}
      <div className="sell-order-form">
        <h2>📤 Create Sell Order</h2>
        <form onSubmit={handleSubmit}>
          <div className="so-form-group">
            <label>Item To Sell</label>
            <select
              value={selectedItemId}
              onChange={(e) => {
                setSelectedItemId(e.target.value);
                setSelectedQuantity(1);
                setSelectedInstanceId(null);
              }}
              required
            >
              <option value="">Select an item...</option>
              {inventory.map((item) => (
                <option key={item.id} value={item.itemId}>
                  {item.Item.name} ({item.quantity} available)
                </option>
              ))}
            </select>
          </div>

          {selectedItemId && (() => {
            const selectedItem = inventory.find((item) => item.itemId === parseInt(selectedItemId));
            const isEquippable = selectedItem ? EQUIPPABLE_CATEGORIES.includes(selectedItem.Item?.itemCategory) : false;
            return (
              <>
                {/* Instance picker, equippable items with multiple copies */}
                {isEquippable && selectedItem && selectedItem.instances.length > 1 && (
                  <div className="so-form-group">
                    <label>Which copy? ({selectedItem.instances.length} available)</label>
                    <div className="so-instance-list">
                      {selectedItem.instances.map((inst, idx) => (
                        <Tooltip
                          key={inst.itemInstanceId}
                          itemId={selectedItem.Item.id}
                          socketedRunes={inst.runes.length > 0 ? inst.runes : undefined}
                        >
                          <div
                            className={`so-instance-card${selectedInstanceId === inst.itemInstanceId ? ' selected' : ''}`}
                            onClick={() => setSelectedInstanceId(inst.itemInstanceId)}
                          >
                            <span className="so-instance-label">Copy #{idx + 1}</span>
                            {inst.runes.length > 0 ? (
                              <span className="so-instance-runes">
                                {inst.runes.map(r => (
                                  <span key={r.runeSlotIndex} className="so-rune-tag">{r.rune.name}</span>
                                ))}
                              </span>
                            ) : (
                              <span className="so-instance-no-runes">No runes</span>
                            )}
                          </div>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quantity */}
                <div className="so-form-group">
                  <label>Quantity</label>
                  {isEquippable ? (
                    <select value={1} disabled title="Equippable items are unique, list one at a time">
                      <option value={1}>1 (unique item)</option>
                    </select>
                  ) : (
                    <select
                      value={selectedQuantity}
                      onChange={(e) => setSelectedQuantity(parseInt(e.target.value))}
                      required
                    >
                      {[...Array(selectedItem?.quantity || 1)].map((_, index) => (
                        <option key={index} value={index + 1}>
                          {index + 1}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </>
            );
          })()}

          <div className="so-form-group">
            <label>Price per unit (silver)</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Enter price..."
              required
            />
          </div>

          <div className="so-form-group">
            <label>Duration & Fee</label>
            <div className="so-duration-row">
              {DURATION_OPTIONS.map(opt => (
                <button
                  key={opt.hours}
                  type="button"
                  className={`so-duration-btn ${duration === opt.hours ? 'active' : ''}`}
                  onClick={() => setDuration(opt.hours)}
                >
                  <span className="so-dur-time">{opt.desc}</span>
                  <span className="so-dur-fee">{opt.fee} silver fee</span>
                </button>
              ))}
            </div>
          </div>

          {price && selectedItemId && (
            <div className="so-summary">
              <div className="so-summary-row">
                <span>Listing fee</span>
                <span>{selectedFee} silver</span>
              </div>
              <div className="so-summary-row">
                <span>Total ask ({selectedQuantity}x @ {price})</span>
                <span>{(parseInt(price) || 0) * selectedQuantity} silver</span>
              </div>
            </div>
          )}

          <button type="submit" className="so-submit-btn" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Sell Order'}
          </button>
        </form>
      </div>

      {/* ── Right: Market Info ── */}
      <div className="so-market-info">
        <div className="so-info-panel">
          <h3>� Sell Orders</h3>
          <div className="so-listings-scroll">
            {selectedItemId ? (
              currentListings.length > 0 ? (
                <table className="so-listings-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentListings.map((listing, index) => (
                      <tr key={index}>
                        <td>{listing.itemName || listing.Item?.name || 'Unknown'}</td>
                        <td>{listing.quantity}</td>
                        <td>{listing.price} silver</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="so-empty-text">No sell orders for this item.</p>
              )
            ) : (
              <p className="so-empty-text">Select an item to see sell orders.</p>
            )}
          </div>
        </div>

        <div className="so-info-panel">
          <h3>🔵 Buy Orders</h3>
          <div className="so-listings-scroll">
            {selectedItemId ? (
              currentBuyOrders.length > 0 ? (
                <table className="so-listings-table">
                  <thead>
                    <tr>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Offer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBuyOrders.map((order, index) => (
                      <tr key={index}>
                        <td>{order.Item?.name || 'Unknown'}</td>
                        <td>{order.quantity}</td>
                        <td>{order.price_offer} silver</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="so-empty-text">No buy orders for this item.</p>
              )
            ) : (
              <p className="so-empty-text">Select an item to see buy orders.</p>
            )}
          </div>
        </div>

        <div className="so-info-panel">
          <h3>📈 Recent Sales (30 Days)</h3>
          <div className="so-chart-wrap">
            {salesData.length > 0 ? (
              <Bar data={chartData} options={chartOptions} />
            ) : (
              <p className="so-empty-text">No sales data available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellOrderForm;
